import { useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCompass, FiPause, FiPlay, FiSmartphone } from "react-icons/fi";
import useHeroShowcase from "./useHeroShowcase";
import { HeroAppScene, HeroBooksScene } from "./HeroScenes";
import "./heroShowcase.css";

const scenes = [
	{ id: "discover", label: "Discover", icon: FiCompass, title: "A library in your pocket.", description: "School books and stories, all in one app." },
	{ id: "anywhere", label: "On the go", icon: FiSmartphone, title: "Read wherever life takes you.", description: "Download digital books in the app and read offline.", to: "/downloads", cta: "Get the app" },
];

export default function HeroShowcase() {
	const containerRef = useRef(null);
	const touchStart = useRef(null);
	const stageRef = useRef(null);
	const { active, direction, select, reducedMotion, playing, rotating, setHovered, pause, togglePlaying } = useHeroShowcase(scenes.length, containerRef);
	const scene = scenes[active];
	const moveArtwork = (event) => {
		if (reducedMotion || event.pointerType !== "mouse") return;
		const rect = event.currentTarget.getBoundingClientRect();
		event.currentTarget.style.setProperty("--hero-pointer-x", `${((event.clientX - rect.left) / rect.width - .5) * 12}px`);
		event.currentTarget.style.setProperty("--hero-pointer-y", `${((event.clientY - rect.top) / rect.height - .5) * 8}px`);
	};
	const resetArtwork = () => {
		stageRef.current?.style.setProperty("--hero-pointer-x", "0px");
		stageRef.current?.style.setProperty("--hero-pointer-y", "0px");
	};

	const navigateTabs = (event) => {
		const next = { ArrowLeft: (active + scenes.length - 1) % scenes.length, ArrowRight: (active + 1) % scenes.length, Home: 0, End: scenes.length - 1 }[event.key];
		if (next === undefined) return;
		event.preventDefault();
		select(next);
		event.currentTarget.querySelectorAll('[role="tab"]')[next].focus();
	};
	const startSwipe = (event) => {
		touchStart.current = null;
		if (event.touches?.length > 1 || event.target.closest("a,button,input,label")) return;
		touchStart.current = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
	};
	const endSwipe = (event) => {
		if (!touchStart.current) return;
		const dx = event.changedTouches[0].clientX - touchStart.current.x;
		const dy = event.changedTouches[0].clientY - touchStart.current.y;
		touchStart.current = null;
		if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.2) select(active + (dx < 0 ? 1 : -1));
	};

	return (
		<div ref={containerRef} className="hero-showcase" role="region" aria-roledescription="carousel" aria-label="Explore Akello Library"
			data-rotating={rotating} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
			onFocusCapture={(event) => { if (!event.target.closest('[data-rotation-control]')) pause(); }}>
			<div className="hero-showcase-tabs" role="tablist" aria-label="Explore the Akello experience" onKeyDown={navigateTabs}>
				{scenes.map(({ id, label, icon: Icon }, index) => (
					<button key={id} type="button" role="tab" id={`hero-tab-${id}`} aria-selected={active === index} aria-controls="hero-showcase-panel" tabIndex={active === index ? 0 : -1} onClick={() => select(index)}>
						<Icon aria-hidden="true" />{label}<span key={`${index}-${active}-${rotating}`} className="hero-tab-progress" aria-hidden="true" />
					</button>
				))}
			</div>
			<div id="hero-showcase-panel" role="tabpanel" aria-labelledby={`hero-tab-${scene.id}`} tabIndex={0}>
				<div ref={stageRef} className={`hero-showcase-stage hero-stage-${scene.id}`} onPointerMove={moveArtwork} onPointerLeave={resetArtwork} onTouchStart={startSwipe} onTouchEnd={endSwipe} onTouchCancel={() => { touchStart.current = null; }}>
					<div className="hero-scene-content" data-direction={direction} key={scene.id}>
						{active === 0 && <HeroAppScene />}
						{active === 1 && <HeroBooksScene onInteract={pause} />}
					</div>
				</div>
				<div className="hero-showcase-caption" key={scene.id}>
					<h2>{scene.title}</h2>
					<p>{scene.description}</p>
					{scene.to && <Link to={scene.to}>{scene.cta}<FiArrowRight aria-hidden="true" /></Link>}
				</div>
			</div>
			<div className="hero-showcase-controls">
				<span className="hero-slide-meta"><span><strong>0{active + 1}</strong><span aria-hidden="true"> / </span><span className="home-sr-only"> of </span>0{scenes.length}</span><span className="hero-play-status"><i aria-hidden="true" />{rotating ? "Auto-playing" : playing ? "On hold" : "Paused"}</span></span>
				<div>
					<button type="button" aria-label="Previous showcase slide" onClick={() => select(active - 1)}><FiArrowLeft aria-hidden="true" /></button>
					<button type="button" data-rotation-control disabled={reducedMotion} aria-label={reducedMotion ? "Slideshow paused for reduced motion" : playing ? "Pause slideshow" : "Play slideshow"} title={reducedMotion ? "Automatic motion is off. Use the slide controls to explore." : playing ? "Pause slideshow" : "Play slideshow"} onClick={togglePlaying}>{playing && !reducedMotion ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}</button>
					<button type="button" aria-label="Next showcase slide" onClick={() => select(active + 1)}><FiArrowRight aria-hidden="true" /></button>
				</div>
			</div>
			<p className="home-sr-only" aria-live={playing ? "off" : "polite"}>Slide {active + 1} of {scenes.length}: {scene.label}</p>
		</div>
	);
}
